import React from 'react';
import { useApp } from 'core/data/app';
import styled from 'theme/styled';
import useProfile from 'core/data/profile';
import p from 'translation';
import gql from 'graphql-tag';
import nestedGroups from 'apps/manage/query/nested-groups';
import GroupFragment from 'apps/manage/query/group-fragment';
import { Group, isGroup } from 'apps/manage/types';
import { User, isUserSubset } from 'core/types/user';
import useQuery, { TransformerError } from 'core/hooks/query';
import { Box, Grid } from '@mui/material';
import GroupsOverview from 'apps/manage/components/groups-overview';
import Avatar from 'core/components/avatar';
import { isAdministratorRole } from 'core/permission';
import illustration from './img/olav-in-video-call.svg';

type UserSubset = Pick<
  User,
  'id' | 'firstName' | 'lastName' | 'email' | 'profilePicture'
>;

interface Props {
  children: React.ReactChild;
}

interface WrapperProps {
  firstName: string;
  reason: string;
  showGroups?: boolean;
}

interface Data {
  groups: Group[];
  administrators: UserSubset[];
}

const query = gql`
  query groupsAndAdmins {
    manage {
      administrators {
        id
        firstName
        lastName
        email
        profilePicture { url }
      }

      groups {
        ${nestedGroups}
      }
    }
  }

  ${GroupFragment}
`;

function transformer(data?: any): Data {
  if (!Array.isArray(data?.manage?.administrators)) {
    throw new TransformerError('data?.manage?.administrators is not an array');
  }
  if (!Array.isArray(data?.manage?.groups)) {
    throw new TransformerError('data?.manage?.groups is not an array');
  }
  if (
    !data?.manage?.administrators.every((u: any) =>
      isUserSubset(u, 'id', 'firstName', 'lastName', 'email', 'profilePicture'),
    )
  ) {
    throw new TransformerError(
      'data?.manage?.administrators contains items which not conform to User',
    );
  }
  if (!data?.manage?.groups.every(isGroup)) {
    throw new TransformerError(
      'data?.manage?.groups contains items which not conform to Group',
    );
  }

  return {
    groups: data?.manage?.groups,
    administrators: data?.manage?.administrators,
  };
}

function Administrator({ user }: { user: UserSubset }): JSX.Element {
  return (
    <Box bgcolor='#E7EFFF' borderRadius='5px' padding={2} paddingLeft={4}>
      <Grid container alignItems='center'>
        <Avatar user={user} className='profile-picture' />
        <Grid alignItems='flex-start'>
          <p>
            {`${user.firstName} ${user.lastName}`}
            <br />
            <a href={`mailto:${user.email}`}>{user.email}</a>
          </p>
        </Grid>
      </Grid>
    </Box>
  );
}

function Wrapper({ firstName, reason, showGroups }: WrapperProps): JSX.Element {
  const [res] = useQuery(query, transformer);

  return (
    <>
      <Container>
        <Content>
          <h1>{p.t('pages.no-access.title', { firstName })}</h1>
          <h2>{reason}</h2>
          <p>{p.t('pages.no-access.description')}</p>
          {showGroups && (
            <>
              <h3>{p.t('pages.no-access.your-groups')}</h3>
              <GroupsOverview
                groups={res.data?.groups || []}
                expanded
                onClick={() => {
                  // void
                }}
              />
            </>
          )}
          <img src={illustration} alt='' />
        </Content>
        <Administrators>
          <p>{p.t('pages.no-access.contact-administrator')}</p>
          <ul>
            {res.data?.administrators.map(admin => (
              <li key={admin.id}>
                <Administrator user={admin} />
              </li>
            ))}
          </ul>
        </Administrators>
      </Container>
    </>
  );
}

export default function ValidateGroupAccess({ children }: Props): JSX.Element {
  const [{ firstName, role }] = useProfile();
  const { state } = useApp();

  if (isAdministratorRole(role.id)) {
    return <>{children}</>;
  }

  if (state.groups === 0) {
    return (
      <Wrapper
        firstName={firstName || ''}
        reason={p.t('pages.no-access.no-groups')}
      />
    );
  }

  if (state.frames === 0) {
    return (
      <Wrapper
        firstName={firstName || ''}
        reason={p.t('pages.no-access.no-komps')}
        showGroups
      />
    );
  }

  return <>{children}</>;
}

const Container = styled.div`
  flex: 1 1 auto;
  flex-grow: 1;
`;

const Content = styled.section`
  margin: 0 auto;
  margin-top: 1em;
  padding: 1em;
  text-align: center;
  max-width: 35em;

  img {
    margin-top: 3em;
    width: 80%;
  }
`;

const Administrators = styled.section`
  margin: 0 auto;
  margin-top: 3em;
  max-width: 35em;
  padding: 1em;

  ul {
    padding: 0;
    margin: 0;
  }
  li {
    padding: 0;
    list-style: none;
    margin-bottom: 2em;
    /* max-width: 25em; */
  }

  .profile-picture {
    margin-right: 1em;
    border: 3px solid ${({ theme }) => theme.palette.common.white};
  }
`;
